import React, { useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Expand,
  X
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Color palette for pie charts
const COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#EF4444', // Red
  '#F59E0B', // Yellow
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#EC4899', // Pink
  '#84CC16', // Lime
  '#6366F1'  // Indigo
];

// Fixed mapping for trip statuses to ensure stable, non-random colors
const STATUS_COLOR_MAP = {
  completed: '#10B981', // green
  cancelled: '#F97316', // orange
  diverged: '#EC4899', // pink
  failed: '#EF4444', // red
  pending: '#F59E0B', // yellow
  started: '#3B82F6', // blue
  pickedup: '#8B5CF6'  // purple
};

// Display label overrides for backend keys
const DISPLAY_NAME_MAP = {
  failed: 'Unaccepted'
};

const formatAxisLabel = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
    return value;
  }
  const parsed = Date.parse(value);
  if (!Number.isNaN(parsed)) {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric'
    }).format(new Date(parsed));
  }
  return value;
};

const ChartCard = ({
  title,
  subtitle,
  data = [],
  type = 'line',
  loading = false,
  selectedStatus = null,
  allowSeriesSelector = true
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSeries, setSelectedSeries] = useState([]);
  const displayLabel = (key) => {
    if (!key) return '';
    if (key === 'trips') return 'Total';
    if (DISPLAY_NAME_MAP[key]) return DISPLAY_NAME_MAP[key];
    return String(key).charAt(0).toUpperCase() + String(key).slice(1);
  };
  

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-48"></div>
          </div>
        </div>
        <div className="card-body">
          <div className="animate-pulse">
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Transform data based on type
  const transformData = (data, type) => {
    if (!data || data.length === 0) return [];

    switch (type) {
      case 'line':
      case 'bar':
        return data.map(item => ({
          date: formatAxisLabel(item._id),
          trips: item.count || item.trips || 0,
          completed: item.completed || 0,
          cancelled: item.cancelled || 0,
          diverged: item.diverged || 0,
          failed: item.failed || 0,
          pending: item.pending || 0,
          started: item.started || 0,
          pickedup: item.pickedup || 0,
          revenue: item.revenue || 0
        }));

      case 'pie':
      case 'doughnut':
        return data.map(item => ({
          key: item._id,
          name: DISPLAY_NAME_MAP[item._id] || item._id,
          value: item.count || 0,
          revenue: item.revenue || 0
        }));

      default:
        return data;
    }
  };

  const transformedData = transformData(data, type);

  // precompute totals per series to show counts in selector and detect empty selection
  const computeSeriesTotals = (rows) => {
    const totals = {
      trips: 0,
      completed: 0,
      cancelled: 0,
      diverged: 0,
      failed: 0,
      pending: 0,
      started: 0,
      pickedup: 0,
      revenue: 0
    };
    (rows || []).forEach(r => {
      Object.keys(totals).forEach(k => {
        const v = Number(r[k]) || 0;
        totals[k] += v;
      });
    });
    return totals;
  };

  const seriesTotals = computeSeriesTotals(transformedData);

  // For pie/doughnut charts compute totals per slice name
  const pieSeriesTotals = (type === 'pie' || type === 'doughnut')
    ? (transformedData || []).reduce((acc, r) => {
        const key = r.key || r._id || 'Unknown';
        acc[key] = (acc[key] || 0) + (Number(r.value) || 0);
        return acc;
      }, {})
    : null;

  // stable slice ordering and color mapping for pie/doughnut charts
  const sliceNames = (type === 'pie' || type === 'doughnut')
    ? Array.from(new Set((transformedData || []).map(e => e.key || e._id || 'Unknown')))
    : null;

  const sliceColorMap = sliceNames
    ? sliceNames.reduce((acc, name, i) => {
        const lc = String(name).toLowerCase();
        acc[name] = STATUS_COLOR_MAP[lc] || COLORS[i % COLORS.length];
        return acc;
      }, {})
    : null;

  // Custom tooltip that only shows non-zero values
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // If series selector is disabled, only show the trips series (total trips)
      const relevantPayload = !allowSeriesSelector
        ? [payload.find(p => p.dataKey === 'trips' || p.name === 'Total Trips') || payload[0]]
        : payload.filter(entry => entry.value > 0);

      if (!relevantPayload || relevantPayload.length === 0) return null;

      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{`Date: ${label}`}</p>
          {relevantPayload.map((entry, index) => (
            entry && (
              <p key={index} style={{ color: entry.color }} className="text-sm">
                {`${entry.name || entry.dataKey}: ${entry.value}`}
              </p>
            )
          ))}
        </div>
      );
    }
    return null;
  };

  const renderChart = (height = 250, isExpandedView = false) => {
    if (!transformedData || transformedData.length === 0) {
      return (
        <div className={`flex items-center justify-center text-gray-500`} style={{ height }}>
          <div className="text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No data available</p>
          </div>
        </div>
      );
    }

    // Calculate dynamic interval for x-axis based on data length and view mode
    const calculateInterval = () => {
      const dataLength = transformedData.length;
      if (isExpandedView) {
        // In expanded view, show more labels
        if (dataLength <= 30) return 0;
        if (dataLength <= 60) return Math.ceil(dataLength / 20);
        return Math.ceil(dataLength / 30);
      } else {
        // In normal view, be more conservative
        if (dataLength <= 10) return 0;
        if (dataLength <= 30) return Math.ceil(dataLength / 8);
        return Math.ceil(dataLength / 10);
      }
    };

    const interval = calculateInterval();

    // Determine which series to show
    const defaultSeries = type === 'line'
      ? ['trips', 'completed']
      : type === 'bar'
        ? ['revenue']
        : (type === 'pie' || type === 'doughnut')
          ? ['value']
          : ['trips', 'completed'];

    // Apply selection filtering only for line charts (trip trends).
    // Revenue (bar), pie/doughnut and other charts should ignore the selectedStatus.
    // In expanded view, if no series are selected, fall back to defaults for that chart type.
    // If the selectedStatus is explicitly 'none' we treat that as "no selection" and show an empty graph.
    const seriesToShow = isExpandedView
      ? (!allowSeriesSelector ? ['trips'] : (selectedSeries && selectedSeries.length ? selectedSeries : defaultSeries))
      : (type === 'line'
        ? (!allowSeriesSelector
          ? ['trips']
          : (selectedStatus === 'none'
            ? []
            : (selectedStatus === 'trips'
              ? ['trips', 'completed', 'cancelled']
              : [selectedStatus || 'trips'])))
        : defaultSeries);

    // If non-expanded view requested no series, render an empty placeholder instead of chart
    if (!isExpandedView && Array.isArray(seriesToShow) && seriesToShow.length === 0) {
      return (
        <div className={`flex items-center justify-center text-gray-500`} style={{ height }}>
          <div className="text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No series selected</p>
          </div>
        </div>
      );
    }

    // For pie/doughnut, allow filtering slices by selectedSeries when expanded
    const pieDataToRender = (type === 'pie' || type === 'doughnut')
      ? (isExpandedView && selectedSeries && selectedSeries.length > 0
          ? transformedData.filter(e => selectedSeries.includes(e.key || e._id))
          : transformedData)
      : null;

    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={transformedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                interval={interval} 
                angle={isExpandedView ? -45 : 0}
                textAnchor={isExpandedView ? "end" : "middle"}
                height={isExpandedView ? 100 : 60}
              />
              <YAxis />
                <Tooltip content={<CustomTooltip />} />
              {allowSeriesSelector && <Legend />}
              {seriesToShow.includes('trips') && (
                <Line
                  type="monotone"
                  dataKey="trips"
                  stroke={STATUS_COLOR_MAP['started'] || '#3B82F6'}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Total Trips"
                />
              )}
              {seriesToShow.includes('completed') && (
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke={STATUS_COLOR_MAP['completed']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Completed"
                />
              )}
              {seriesToShow.includes('cancelled') && (
                <Line
                  type="monotone"
                  dataKey="cancelled"
                  stroke={STATUS_COLOR_MAP['cancelled']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Cancelled"
                />
              )}
              {seriesToShow.includes('diverged') && (
                <Line
                  type="monotone"
                  dataKey="diverged"
                  stroke={STATUS_COLOR_MAP['diverged']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Diverged"
                />
              )}
              {seriesToShow.includes('failed') && (
                <Line
                  type="monotone"
                  dataKey="failed"
                  stroke={STATUS_COLOR_MAP['failed']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name={DISPLAY_NAME_MAP['failed'] || 'Failed'}
                />
              )}
              {seriesToShow.includes('pending') && (
                <Line
                  type="monotone"
                  dataKey="pending"
                  stroke={STATUS_COLOR_MAP['pending']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Pending"
                />
              )}
              {seriesToShow.includes('started') && (
                <Line
                  type="monotone"
                  dataKey="started"
                  stroke={STATUS_COLOR_MAP['started']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Started"
                />
              )}
              {seriesToShow.includes('pickedup') && (
                <Line
                  type="monotone"
                  dataKey="pickedup"
                  stroke={STATUS_COLOR_MAP['pickedup']}
                  isAnimationActive={true}
                  strokeWidth={2}
                  name="Picked Up"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={transformedData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="date" 
                interval={interval}
                angle={isExpandedView ? -45 : 0}
                textAnchor={isExpandedView ? "end" : "middle"}
                height={isExpandedView ? 100 : 60}
              />
              <YAxis />
              <Tooltip />
              {allowSeriesSelector && <Legend />}
              {seriesToShow.includes('revenue') && (
                <Bar dataKey="revenue" fill="#3B82F6" name="Revenue (₹)" isAnimationActive={true} />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={pieDataToRender}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={Math.min(height * 0.3, 120)}
                fill="#8884d8"
                dataKey="value"
                isAnimationActive={false}
                animationDuration={0}
              >
                {(pieDataToRender || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={sliceColorMap ? (sliceColorMap[entry.key || entry._id] || COLORS[index % COLORS.length]) : COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              {allowSeriesSelector && <Legend verticalAlign="bottom" height={36} />}
            </PieChart>
          </ResponsiveContainer>
        );

      case 'doughnut':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={pieDataToRender}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={false}
                outerRadius={Math.min(height * 0.3, 120)}
                innerRadius={Math.min(height * 0.15, 60)}
                fill="#8884d8"
                dataKey="value"
                isAnimationActive={false}
                animationDuration={0}
              >
                {(pieDataToRender || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={sliceColorMap ? (sliceColorMap[entry.key || entry._id] || COLORS[index % COLORS.length]) : COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              {allowSeriesSelector && <Legend verticalAlign="bottom" height={36} />}
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <div className="flex items-center justify-center text-gray-500" style={{ height }}>
            <div className="text-center">
              <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>Chart type not supported</p>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  // initialize series selection when opening expanded view
                  const computeDefault = () => {
                    if (!allowSeriesSelector) return ['trips'];
                    if (type === 'line') {
                      // if a non-trips status is selected on the dashboard, open with that single series
                      if (selectedStatus && selectedStatus !== 'trips') {
                        return [selectedStatus];
                      }
                      // otherwise include Total Trips and any series that have non-zero totals
                      const keys = ['trips'];
                      ['completed','cancelled','diverged','failed','pending','started','pickedup'].forEach(k => {
                        if ((seriesTotals && (seriesTotals[k] || 0)) > 0) keys.push(k);
                      });
                      return Array.from(new Set(keys));
                    }
                    if (type === 'bar') return ['revenue'];
                    if (type === 'pie' || type === 'doughnut') {
                      // initialize with slices that have non-zero totals, or all slice keys if none
                      const keys = (transformedData || []).map(e => e.key || e._id).filter(Boolean);
                      const nonZero = keys.filter(k => (pieSeriesTotals && (pieSeriesTotals[k] || 0)) > 0);
                      return nonZero.length ? nonZero : keys;
                    }
                    return ['value'];
                  };

                  setSelectedSeries(computeDefault());
                  setIsExpanded(true);
                }}
                className="p-2 rounded-lg bg-primary-50 hover:bg-primary-100 transition-colors"
                title="Expand chart"
              >
                <Expand className="h-4 w-4 text-primary-600" />
              </button>
              <div className="p-2 rounded-lg bg-primary-50">
                {type === 'line' && <TrendingUp className="h-5 w-5 text-primary-600" />}
                {type === 'bar' && <BarChartIcon className="h-5 w-5 text-primary-600" />}
                {(type === 'pie' || type === 'doughnut') && <PieChartIcon className="h-5 w-5 text-primary-600" />}
                {!['line', 'bar', 'pie', 'doughnut'].includes(type) && <BarChart3 className="h-5 w-5 text-primary-600" />}
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          {renderChart()}
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full h-full max-w-7xl max-h-full overflow-hidden">
            <div className="flex flex-col border-b">
              <div className="flex items-center justify-between p-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                  <p className="text-sm text-gray-500">{subtitle}</p>
                </div>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Close expanded view"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              {/* Series selector controls (for line charts and pie/doughnut in expanded modal) */}
              {allowSeriesSelector && (type === 'line' || type === 'pie' || type === 'doughnut') && (
                <div className="p-3 border-t flex flex-wrap items-center gap-2">
                  {type === 'line' ? (
                    ['trips','completed','cancelled','diverged','failed','pending','started','pickedup'].map(key => (
                      <label key={key} className={`inline-flex items-center px-3 py-1 rounded cursor-pointer border ${selectedSeries.includes(key) ? 'bg-primary-50 border-primary-200' : 'bg-white border-gray-200'}`}>
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={selectedSeries.includes(key)}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedSeries(s => Array.from(new Set([...s, key])));
                            else setSelectedSeries(s => s.filter(x => x !== key));
                          }}
                        />
                        <span className="capitalize text-sm">{displayLabel(key)}</span>
                        <span className="ml-2 text-xs text-gray-500">({seriesTotals[key]?.toLocaleString() || 0})</span>
                      </label>
                    ))
                  ) : (
                    // pie/doughnut: list slice names from transformedData
                    (transformedData || []).map((entry, idx) => {
                      const key = entry.key || entry._id || `slice-${idx}`;
                      const label = entry.name || key;
                      return (
                        <label key={key} className={`inline-flex items-center px-3 py-1 rounded cursor-pointer border ${selectedSeries.includes(key) ? 'bg-primary-50 border-primary-200' : 'bg-white border-gray-200'}`}>
                          <input
                            type="checkbox"
                            className="mr-2"
                            checked={selectedSeries.includes(key)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedSeries(s => Array.from(new Set([...s, key])));
                              else setSelectedSeries(s => s.filter(x => x !== key));
                            }}
                          />
                          <span className="capitalize text-sm">{label}</span>
                          <span className="ml-2 text-xs text-gray-500">({(pieSeriesTotals && (pieSeriesTotals[key] || 0)).toLocaleString?.() || 0})</span>
                        </label>
                      );
                    })
                  )}
                  <div className="ml-auto text-sm text-gray-500">Select series to display</div>
                </div>
              )}
            </div>
            <div className="p-6 h-full overflow-auto">
              <div className="h-full" style={{ minHeight: '500px' }}>
                {renderChart(Math.max(500, window.innerHeight - 200), true)}
                {allowSeriesSelector && (
                  <>
                    {(!selectedSeries || selectedSeries.length === 0) && (
                      <div className="text-center text-gray-500 mt-4">No series selected</div>
                    )}

                    {/* If selected series exist but all totals are zero, show no-data message */}
                    {selectedSeries && selectedSeries.length > 0 && (
                      (type === 'line' && selectedSeries.every(k => (seriesTotals[k] || 0) === 0)) ||
                      ((type === 'pie' || type === 'doughnut') && selectedSeries.every(k => ((pieSeriesTotals && (pieSeriesTotals[k] || 0)) === 0)))
                    ) && (
                      <div className="text-center text-gray-500 mt-4">No data for selected series</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChartCard; 